import { useCallback, useEffect, useState } from 'react'
import { Cobros, useTotalesDelDia } from './Cobros.js'
import { Caja, type PendienteArqueo, type Turno } from './Caja.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { guaranies } from '../datos/formato.js'

/**
 * Cobros y caja, en una sola pantalla.
 *
 * Eran dos pestañas hermanas que no se hablaban, y eso hacía posible el absurdo
 * de cerrar la caja y seguir cobrando en la pantalla de al lado. Son la misma
 * pregunta —qué entró hoy— vista dos veces: por paciente y por cajón.
 *
 * Desde T26 comparten la cabecera: una fila de tres números —cobrado, por
 * cobrar y en el cajón— visible en las dos solapas. El tercero sale del turno
 * abierto, que se levanta acá y se le pasa a `Caja` por props: un solo fetch,
 * un solo dato, y la cabecera no puede contradecir a la solapa.
 */

type Solapa = 'cobros' | 'caja'

export interface DineroProps {
  dia: string
  onDia: (dia: string) => void
  citaEnfocada?: string | null
  onVerPaciente?: ((clienteId: string) => void) | undefined
}

export function Dinero({ dia, onDia, citaEnfocada, onVerPaciente }: DineroProps) {
  const [solapa, setSolapa] = useState<Solapa>('cobros')
  const [turno, setTurno] = useState<Turno | null>(null)
  const [pendientes, setPendientes] = useState<PendienteArqueo[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // El turno de caja es la excepción de red declarada: el arqueo cuenta lo que
  // el servidor registró, no lo que alcanzó a llegar a este dispositivo. Vive
  // acá porque la cabecera lo muestra en las dos solapas; `Caja` sólo lo pinta.
  //
  // Los pendientes de arqueo salen del mismo lado y se usan en los dos: la
  // franja ámbar muestra su cantidad y `Caja` la lista. Un solo fetch, un solo
  // dato: si la franja y la solapa se trajeran la cuenta por separado, podrían
  // contradecirse.
  const cargar = useCallback(async () => {
    setCargando(true)
    setError(null)
    try {
      // Sin `sucursalId`: con una sola sucursal la resuelve el servidor, que
      // es quien las conoce. La PWA no las replica.
      const [resTurno, resPendientes] = await Promise.all([
        fetch('/api/caja/turnos/actual', { credentials: 'include' }),
        fetch('/api/caja/pendientes-arqueo', { credentials: 'include' }),
      ])
      if (!resTurno.ok || !resPendientes.ok) {
        const fallida = resTurno.ok ? resPendientes : resTurno
        const cuerpo = (await fallida.json().catch(() => ({}))) as { mensaje?: string }
        throw new Error(cuerpo.mensaje ?? 'No pudimos consultar la caja')
      }
      const cuerpoTurno = (await resTurno.json()) as { turno: Turno | null }
      const cuerpoPendientes = (await resPendientes.json()) as { pendientes: PendienteArqueo[] }
      setTurno(cuerpoTurno.turno)
      setPendientes(cuerpoPendientes.pendientes)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos consultar la caja')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const { cobrado, porCobrar } = useTotalesDelDia(dia)

  return (
    <div>
      <TituloPagina
        titulo="Caja"
        bajada="Cobrar no depende del turno: el turno agrupa lo cobrado, no lo autoriza."
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Total etiqueta="Cobrado hoy" monto={cobrado} />
        <Total etiqueta="Por cobrar" monto={porCobrar} alerta={porCobrar > 0} />
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="txt-meta uppercase tracking-wide">En el cajón</p>
          {/* Sin turno no es un error: no tener la caja abierta ya no impide
              cobrar. Gris y quieto, no una alarma. */}
          {turno ? (
            <p className="mt-1 text-[24px] font-medium tabular-nums text-slate-900">
              {guaranies(turno.esperado)}
            </p>
          ) : (
            <p className="mt-1 text-[24px] font-medium tabular-nums text-slate-400">
              caja cerrada
            </p>
          )}
        </div>
      </div>

      {/* La única cosa que amerita destacarse: plata registrada que nadie
          contó. Sin turno de caja no es "pendiente de pago", es plata que
          quedó sin que nadie la viera. */}
      {pendientes.length > 0 && (
        <div
          role="status"
          className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
        >
          <p className="text-sm font-medium text-amber-900">
            {`${pendientes.length} cobro${pendientes.length === 1 ? '' : 's'} sin turno de caja`}
          </p>
          <button
            type="button"
            onClick={() => setSolapa('caja')}
            className="shrink-0 text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-950"
          >
            Ver
          </button>
        </div>
      )}

      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {(
          [
            ['cobros', 'Cobros del día'],
            ['caja', 'Caja'],
          ] as [Solapa, string][]
        ).map(([id, etiqueta]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSolapa(id)}
            aria-current={solapa === id ? 'page' : undefined}
            // El subrayado y no un fondo: son solapas dentro de una pantalla,
            // no navegación de la clínica. Si se vieran como la barra de la
            // izquierda, competirían con ella.
            className={`-mb-px h-9 border-b-2 px-3 text-sm transition ${
              solapa === id
                ? 'border-teal-700 font-medium text-teal-800'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {solapa === 'cobros' ? (
        <Cobros
          dia={dia}
          onDia={onDia}
          citaEnfocada={citaEnfocada ?? null}
          {...(onVerPaciente ? { onVerPaciente } : {})}
        />
      ) : (
        <Caja
          turno={turno}
          pendientes={pendientes}
          cargando={cargando}
          error={error}
          onCambio={cargar}
          onError={setError}
        />
      )}
    </div>
  )
}

function Total({ etiqueta, monto, alerta }: { etiqueta: string; monto: number; alerta?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="txt-meta uppercase tracking-wide">{etiqueta}</p>
      <p
        className={`mt-1 text-[24px] font-medium tabular-nums ${
          alerta ? 'text-amber-700' : monto > 0 ? 'text-slate-900' : 'text-slate-400'
        }`}
      >
        {guaranies(monto)}
      </p>
    </div>
  )
}
